import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AppShell from "@/layout/AppShell";
import DashboardLayout from "@/layout/DashboardLayout";
import PublicLayout from "@/layout/PublicLayout";
import ModuleGuestRoute from "@/router/ModuleGuestRoute";
import ModuleProtectedRoute from "@/router/ModuleProtectedRoute";
import { renderSharedPublicRoutes } from "@/router/SharedPublicRoutes";
import StripPrefixRedirect from "@/router/StripPrefixRedirect";

const ProviderLoginPage = lazy(() => import("@/modules/portal/provider/pages/ProviderLoginPage"));
const ProviderForgotPasswordPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderForgotPasswordPage"),
);
const ProviderResetPasswordPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderResetPasswordPage"),
);
const ProviderChangePasswordPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderChangePasswordPage"),
);
const ProviderLandingPage = lazy(() => import("@/modules/portal/provider/pages/ProviderLandingPage"));
const ProviderSearchAnalyticsPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderSearchAnalyticsPage"),
);
const ProviderAppointmentsPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderAppointmentsPage"),
);
const ProviderLeadsPage = lazy(() => import("@/modules/portal/provider/pages/ProviderLeadsPage"));
const ProviderMessagesPage = lazy(() => import("@/modules/portal/provider/pages/ProviderMessagesPage"));
const ProviderLeadDetailPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderLeadDetailPage"),
);
const ProviderSchedulePage = lazy(() => import("@/modules/portal/provider/pages/ProviderSchedulePage"));
const ProviderPatientDetailPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderPatientDetailPage"),
);
const ProviderBillingPage = lazy(() => import("@/modules/portal/provider/pages/ProviderBillingPage"));
const ProviderSubscriptionPlansPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderSubscriptionPlansPage"),
);
const ProviderProfilePage = lazy(() => import("@/modules/portal/provider/pages/ProviderProfilePage"));
const ProviderLeadScoringPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderLeadScoringPage"),
);
const ProviderSupportTicketsPage = lazy(
  () => import("@/modules/portal/provider/pages/ProviderSupportTicketsPage"),
);
const ProviderRouter = () => (
  <Suspense fallback={<div className="page-container page-padding">Loading...</div>}>
    <Routes>
      <Route element={<AppShell />}>
        <Route element={<PublicLayout />}>
          {renderSharedPublicRoutes(false)}
        </Route>

        <Route path="/forgot-password" element={<ProviderForgotPasswordPage />} />
        <Route path="/reset-password" element={<ProviderResetPasswordPage />} />
        <Route path="/providers/forgot-password" element={<ProviderForgotPasswordPage />} />
        <Route path="/providers/reset-password" element={<ProviderResetPasswordPage />} />
        <Route path="/provider/forgot-password" element={<ProviderForgotPasswordPage />} />
        <Route path="/provider/reset-password" element={<ProviderResetPasswordPage />} />
        <Route path="/user/forgot-password" element={<ProviderForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ProviderResetPasswordPage />} />

        <Route
          element={
            <ModuleGuestRoute>
              <DashboardLayout />
            </ModuleGuestRoute>
          }
        >
          <Route path="/login" element={<ProviderLoginPage />} />
          <Route path="/user/login" element={<Navigate to="/login" replace />} />
          <Route path="/admin/login" element={<Navigate to="/login" replace />} />
          <Route path="/admins/login" element={<Navigate to="/login" replace />} />
          <Route path="/provider/login" element={<Navigate to="/login" replace />} />
          <Route path="/providers/login" element={<Navigate to="/login" replace />} />
        </Route>

        <Route
          element={
            <ModuleProtectedRoute allowedRoles={["provider", "staff"]} loginRoute="/login">
              <DashboardLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<ProviderLandingPage />} />
          <Route path="/analytics/search-intelligence" element={<ProviderSearchAnalyticsPage />} />
          <Route path="/appointments" element={<ProviderAppointmentsPage />} />
          <Route path="/leads" element={<ProviderLeadsPage />} />
          <Route path="/messages" element={<ProviderMessagesPage />} />
          <Route path="/messages/:id" element={<ProviderMessagesPage />} />
          <Route path="/change-password" element={<ProviderChangePasswordPage />} />
          <Route path="/leads/:id" element={<ProviderLeadDetailPage />} />
          <Route path="/schedule" element={<ProviderSchedulePage />} />
          <Route path="/patients/:id" element={<ProviderPatientDetailPage />} />
        </Route>

        <Route
          element={
            <ModuleProtectedRoute allowedRoles={["provider"]} loginRoute="/login">
              <DashboardLayout />
            </ModuleProtectedRoute>
          }
        >
          <Route path="/billing" element={<ProviderBillingPage />} />
          <Route path="/subscription" element={<ProviderSubscriptionPlansPage />} />
          <Route path="/subscriptions" element={<Navigate to="/subscription" replace />} />
          <Route path="/profile" element={<ProviderProfilePage />} />
          <Route path="/settings/lead-scoring" element={<ProviderLeadScoringPage />} />
          <Route path="/support" element={<ProviderSupportTicketsPage />} />
          <Route path="/staff" element={<Navigate to="/profile" replace />} />
          <Route path="/settings" element={<Navigate to="/profile" replace />} />
          <Route path="/management" element={<Navigate to="/profile" replace />} />
          <Route path="/settings/manage-speciality" element={<Navigate to="/profile" replace />} />
          <Route path="/settings/manage-insurance" element={<Navigate to="/profile" replace />} />
        </Route>

        <Route path="/provider/*" element={<StripPrefixRedirect prefix="/provider" fallbackTo="/dashboard" />} />
        <Route path="/providers/*" element={<StripPrefixRedirect prefix="/providers" fallbackTo="/dashboard" />} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  </Suspense>
);

export default ProviderRouter;
